import { useEffect, useState } from "react";

/**
 * Hook to detect if user prefers reduced motion
 * Returns true if prefers-reduced-motion is set
 */
export function useMotionPreferences() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return prefersReducedMotion;
}

/**
 * Hook to detect if device is desktop (has mouse/cursor)
 * Returns false for touch devices (mobile/tablet)
 */
export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    // Check if device supports hover (typically desktop)
    const hasHover = window.matchMedia("(hover: hover)").matches;
    
    // Check if device has coarse pointer (typically touch)
    const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
    
    setIsDesktop(hasHover && !hasCoarsePointer);
  }, []);

  return isDesktop;
}

/**
 * Combined hook to check if animations should be enabled
 * Returns true if animations are safe to use (desktop + no motion preference)
 */
export function useEnhancedAnimations() {
  const prefersReducedMotion = useMotionPreferences();
  const isDesktop = useIsDesktop();

  return !prefersReducedMotion && isDesktop;
}
